import React, { useEffect, useMemo } from 'react';

// Auto-detect environment from current hostname
const getEnvironmentInfo = () => {
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return { name: 'development', label: 'Local Development (localhost:8000)', badge: 'LOCAL', isProduction: false };
  } else if (hostname === 'staging.optionsleveling.com') {
    return { name: 'staging', label: 'Staging (Railway)', badge: 'STAGING', isProduction: true };
  } else if (hostname === 'app.optionsleveling.com') {
    return { name: 'production', label: 'Production (Railway)', badge: 'PROD', isProduction: true };
  } else if (hostname === 'optionsleveling.com') {
    return { name: 'legacy', label: 'Legacy (Cloud Run)', badge: 'LEGACY', isProduction: true };
  }
  // Fallback for any other domain
  return { name: 'unknown', label: hostname, badge: 'OTHER', isProduction: true };
};

// test_account_status codes from admin_tests/runner_safety.py. 'ok' and
// 'ok_dev_auto' (development manages its own account) mean ready.
const READY_ACCOUNT_STATUSES = ['ok', 'ok_dev_auto'];
const ACCOUNT_STATUS_TEXT = {
  missing_env: 'PROD_TEST_USERNAME and PROD_TEST_PASSWORD are not set on the backend.',
  invalid_env: 'PROD_TEST_USERNAME / PROD_TEST_PASSWORD are set but refused (a retired name or a weak password).',
  missing_account: "The test account doesn't exist yet.",
  inactive: 'The test account is inactive (it was disabled or neutralized).',
  not_staff: 'The test account is not staff.',
  superuser: 'The test account is a superuser, which the runner refuses to use.',
  email_mismatch: "The test account doesn't have its @test.local email, so it looks like a real account.",
  not_in_group: "The test account isn't in the admin_tests_bot group.",
};
const ENV_STATUSES = ['missing_env', 'invalid_env'];

const accountStatusHint = (status, serverMode) => {
  if (serverMode === 'development') {
    // Only 'invalid_env' reaches here locally: without the env vars the
    // local runner uses its own account
    return 'Fix or remove PROD_TEST_USERNAME / PROD_TEST_PASSWORD in the local .env, then restart the backend.';
  }
  if (ENV_STATUSES.includes(status)) {
    return 'Set valid PROD_TEST_USERNAME / PROD_TEST_PASSWORD backend variables, then run python manage.py setup_prod_test_account.';
  }
  return 'Run python manage.py setup_prod_test_account on the backend (it creates or restores the account), then --check.';
};

function TestRunner({
  environment,
  setEnvironment,
  testTypes,
  selectedTests,
  toggleTestSelection,
  selectAllTests,
  deselectAllTests,
  selectCategory,
  handleRunTests,
  isRunning,
  serverInfo,
  maxTestsPerRun
}) {
  // Auto-detect environment on mount. It's sent with the run for display
  // only: the server decides the mode (serverInfo.runner_mode).
  const envInfo = useMemo(() => getEnvironmentInfo(), []);

  useEffect(() => {
    // Set environment based on detected hostname
    setEnvironment(envInfo.name);
  }, [envInfo.name, setEnvironment]);

  const serverMode = serverInfo?.runner_mode || null;
  // Before the health check answers, fall back to the hostname
  const isProductionMode = serverMode ? serverMode === 'production' : envInfo.isProduction;
  const runnerOff = serverInfo?.runner_enabled === false;
  const accountStatus = serverInfo?.test_account_status;
  const accountNotReady = Boolean(accountStatus) && !READY_ACCOUNT_STATUSES.includes(accountStatus);
  const overCap = Boolean(maxTestsPerRun) && selectedTests.length > maxTestsPerRun;

  // Group tests by category
  const testsByCategory = testTypes.reduce((acc, test) => {
    if (!acc[test.category]) {
      acc[test.category] = [];
    }
    acc[test.category].push(test);
    return acc;
  }, {});

  return (
    <div className="test-runner">
      <div className="runner-controls">
        <div>
          <div className="environment-selector">
            <label>Detected from hostname:</label>
            <span className="env-info">{envInfo.label}</span>
            <span className={`env-badge ${envInfo.name}`}>
              {envInfo.badge}
            </span>
          </div>
          {serverMode && (
            <div className="environment-selector" style={{ marginTop: 10 }}>
              <label>Server mode:</label>
              <span className={`env-badge ${serverMode}`}>
                {serverMode.toUpperCase()}
              </span>
              <span className="env-info">
                {serverMode === 'production'
                  ? 'Safety rules on: no user creation; destructive tests skipped'
                  : 'Local runner: manages its own test account'}
              </span>
            </div>
          )}
        </div>

        <div className="selection-controls">
          <button onClick={selectAllTests} disabled={isRunning}>
            Select All
          </button>
          <button onClick={deselectAllTests} disabled={isRunning}>
            Clear Selection
          </button>
        </div>
      </div>

      {runnerOff && (
        <div className="error-message">
          The test runner is off on this server (ADMIN_TESTS_RUNNER_ENABLED). Runs are refused
          until the backend flag is set to true.
        </div>
      )}

      {accountNotReady && (
        <div className="warning-message">
          <p>⚠️ <strong>Test account not ready</strong> ({accountStatus})</p>
          <p>{ACCOUNT_STATUS_TEXT[accountStatus] || 'The shared test account failed its checks.'}</p>
          <p>{accountStatusHint(accountStatus, serverMode)}</p>
        </div>
      )}

      <div className="test-selection">
        <h3>Select Tests to Run</h3>
        {Object.entries(testsByCategory).map(([category, tests]) => (
          <div key={category} className="test-category">
            <div className="category-header">
              <h4>{category}</h4>
              <button
                className="select-category-btn"
                onClick={() => selectCategory(category)}
                disabled={isRunning}
              >
                Select All {category}
              </button>
            </div>
            <div className="test-list">
              {tests.map(test => (
                <label key={test.id} className="test-item">
                  <input
                    type="checkbox"
                    checked={selectedTests.includes(test.id)}
                    onChange={() => toggleTestSelection(test.id)}
                    disabled={isRunning}
                  />
                  <span className="test-name">{test.name}</span>
                  <span className="test-info" title={test.description}>
                    ℹ️
                  </span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="run-controls">
        <button
          className="run-button"
          onClick={handleRunTests}
          disabled={isRunning || selectedTests.length === 0 || overCap}
        >
          {isRunning ? 'Running Tests...' : `Run ${selectedTests.length || 'All'} Tests`}
        </button>
        {overCap && (
          <div className="warning-message">
            <p>
              ⚠️ {selectedTests.length} tests selected. Production runs are capped at {maxTestsPerRun} tests;
              run one category at a time.
            </p>
          </div>
        )}
        {!isProductionMode && (
          <div className="info-message">
            <p>ℹ️ <strong>Local Testing:</strong></p>
            <p>• Tests run against your local Docker containers (localhost:8000)</p>
            <p>• All test types are safe to run in development</p>
            <p>• The runner creates and syncs its own local test account</p>
            <p>• Test data is created and cleaned up automatically</p>
          </div>
        )}
        {isProductionMode && (
          <div className="warning-message">
            <p>⚠️ <strong>Production-Mode Testing ({envInfo.badge}):</strong></p>
            <p>• The server decides the mode and the API URL (ADMIN_TESTS_BASE_URL); the hostname above is for display only</p>
            <p>• Tests run as the shared test account (PROD_TEST_USERNAME); the runner never creates or changes users</p>
            <p>• Tests that need extra users, send real email or write billing data are skipped, with the reason in the results</p>
            <p>• One run at a time{maxTestsPerRun ? `, at most ${maxTestsPerRun} tests each` : ''}</p>
            <p>• Test data (TEST_ or timestamped names) stays on the test account and each suite cleans it up</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TestRunner;